import {
  ApdmPlateCouponBatchRedeemLine,
  ApdmPlateCouponItem,
  CouponBenefit,
  CouponBenefitType,
  CouponRecipientQrInput,
} from '@euler/model/apdm';
import { URL } from 'react-native-url-polyfill';

export function parseApdmCouponQr(
  value: string,
): CouponRecipientQrInput | null {
  if (value.length > 4096) return null;
  try {
    const url = new URL(value.trim());
    if (url.protocol !== 'https:' || url.username || url.password || url.hash)
      return null;
    if (
      url.searchParams.getAll('id').length !== 1 ||
      url.searchParams.getAll('r').length !== 1
    )
      return null;
    const couponCampaignId = url.searchParams.get('id')!;
    const recipientToken = url.searchParams.get('r')!;
    if (
      !/^cpc_[a-z0-9]{26}$/.test(couponCampaignId) ||
      !/^[a-zA-Z0-9_-]{40}$/.test(recipientToken)
    )
      return null;
    return { couponCampaignId, recipientToken };
  } catch {
    return null;
  }
}

export function apdmCouponItemUsable(item: ApdmPlateCouponItem): boolean {
  return item.claimableCouponIds.length > 0;
}

/** How many of each item to redeem, by item id. */
export type ApdmCouponPicks = Readonly<Record<string, number>>;

/** What a fresh coupon starts with: its only item, when one is left. */
export function initialApdmCouponPicks(
  items: readonly ApdmPlateCouponItem[],
): ApdmCouponPicks {
  const usable = items.filter(apdmCouponItemUsable);
  return usable.length === 1 && usable[0].claimableCouponIds.length === 1
    ? { [usable[0].itemId]: 1 }
    : {};
}

/** The exact coupons a set of picks redeems: each item's first ones. */
export function apdmCouponBatchLines(
  items: readonly ApdmPlateCouponItem[],
  picks: ApdmCouponPicks,
): ApdmPlateCouponBatchRedeemLine[] {
  return items.flatMap(item => {
    const count = picks[item.itemId] ?? 0;
    if (count < 1) return [];
    if (count > item.claimableCouponIds.length)
      throw new Error('Coupon item has fewer left than picked');
    return [
      {
        itemId: item.itemId,
        couponIds: item.claimableCouponIds.slice(0, count),
      },
    ];
  });
}

export function couponBenefitText(benefit: CouponBenefit): string {
  switch (benefit.type) {
    case CouponBenefitType.CashOff:
      return `${benefit.minSpend ? `满${benefit.minSpend}元` : ''}减${
        benefit.amount
      }元`;
    case CouponBenefitType.PercentOff:
      return `${(100 - benefit.percent) / 10}折${
        benefit.maxDiscount ? `，最高优惠${benefit.maxDiscount}元` : ''
      }`;
    case CouponBenefitType.FreeService:
      return `免费${benefit.serviceName}`;
    case CouponBenefitType.FreeItem:
      return `赠送${benefit.itemName}`;
    case CouponBenefitType.GiftOnPurchase:
      return `买${benefit.buy}赠${benefit.get}`;
  }
}
